/**
 * The generic reader behind most of this console.
 *
 * Roughly forty control-plane tables are worth looking at and only a handful
 * deserve a bespoke screen, so those forty share this one: it derives its
 * columns from the rows it was given, renders lifecycle states as badges and
 * timestamps as real dates, filters across every field, and opens the full
 * row - including nested JSONB evidence, which no table cell can show - in a
 * drawer. Deriving columns rather than declaring them means a screen keeps
 * working, and keeps showing everything, the day a column is added.
 *
 * `rowActions` is the one thing it does beyond reading. Several control areas
 * are ordinary tables whose rows are also the subject of an audited control -
 * restores are the standing example - and before this slot existed those
 * actions were defined in `ACTION_FIELDS` and reachable from nowhere. A tab
 * opts in by naming its area in the registry; the slot itself knows nothing
 * about controls, so anything a screen can render per row fits through it.
 */

import { useDeferredValue, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SearchLg, XClose } from '@untitledui/icons';
import { Button } from '../components/base/buttons/button';
import { Input } from '../components/base/input/input';
import { cx } from '../utils/cx';
import {
  formatCell,
  formatFull,
  humanize,
  isAbbreviated,
  isEmptyValue,
  isMultiline,
  isTimestampKey,
  formatRelative,
  recordIdentity,
  recordKeys,
} from './format';
import { isStatusKey, toneForHttpStatus } from './status';
import { orderColumnsByValue } from './columns';
import { Callout, StatusBadge } from './primitives';

export type Record_ = Record<string, unknown>;

/**
 * Declared order first, then everything else banded by information value -
 * identity, state, measure, time, incidental - rather than by whatever order
 * the payload happened to serialise. See `columns.ts` for the rule.
 */
function orderColumns(records: readonly Record_[], preferred: readonly string[] = []): string[] {
  return orderColumnsByValue(recordKeys(records), preferred);
}

/**
 * Columns that carry nothing for any row on screen.
 *
 * A wide payload with several always-null fields turned a single row of real
 * data into a sideways scroll of em dashes - `error_code —`, `error_detail —`,
 * `feature_tags —` - pushing the fields that say what happened off the right
 * edge. A column earns its width by having a value somewhere; the drawer still
 * shows the whole row, so nothing is lost by leaving it out here.
 *
 * Declared columns are kept regardless: a screen that named a column meant it,
 * and its absence would read as the field having been removed.
 */
function withoutEmptyColumns(
  columns: readonly string[],
  records: readonly Record_[],
  declared: readonly string[],
): string[] {
  if (!records.length) return [...columns];
  return columns.filter(
    (column) =>
      declared.includes(column) || records.some((record) => !isEmptyValue(record[column])),
  );
}

function searchText(record: Record_): string {
  return Object.values(record)
    .map((value) => (typeof value === 'object' && value !== null ? JSON.stringify(value) : value))
    .join(' ')
    .toLowerCase();
}

function hashParameters(): URLSearchParams {
  if (typeof location === 'undefined') return new URLSearchParams();
  return new URLSearchParams(location.hash.split('?')[1] ?? '');
}

function replaceHashParameters(values: Record<string, string | undefined>) {
  if (typeof location === 'undefined' || typeof history === 'undefined') return;
  const [path = ''] = location.hash.split('?');
  const parameters = hashParameters();
  for (const [key, value] of Object.entries(values)) {
    if (value) parameters.set(key, value);
    else parameters.delete(key);
  }
  const query = parameters.toString();
  history.replaceState(
    null,
    '',
    `${location.pathname}${location.search}${path}${query ? `?${query}` : ''}`,
  );
}

function CellValue({ column, value }: { column: string; value: unknown }) {
  if (isEmptyValue(value)) return <span className="text-quaternary">—</span>;
  if (isStatusKey(column) && typeof value === 'string') return <StatusBadge value={value} />;
  if (column === 'status_code' && typeof value === 'number') {
    const tone = toneForHttpStatus(value);
    return (
      <span
        className={cx(
          'font-mono text-xs font-semibold',
          tone === 'error' && 'text-error-primary',
          tone === 'warning' && 'text-warning-primary',
          tone === 'success' && 'text-success-primary',
        )}
      >
        {value}
      </span>
    );
  }
  if (typeof value === 'boolean') {
    return <StatusBadge value={value ? 'yes' : 'no'} />;
  }
  if (/_percent$/.test(column) && typeof value === 'number') {
    const width = Math.max(0, Math.min(100, value));
    return (
      <span className="flex min-w-28 items-center gap-2" title={`Raw value: ${value}`}>
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-secondary">
          <span
            className="block h-full rounded-full bg-brand-solid"
            style={{ width: `${width}%` }}
          />
        </span>
        <span className="font-mono text-xs">{formatCell(column, value)}</span>
      </span>
    );
  }
  const text = formatCell(column, value);
  const monospace = isAbbreviated(String(value)) || typeof value === 'number';
  return (
    <span
      className={cx('block truncate', monospace && 'font-mono text-xs')}
      title={`Raw value: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`}
    >
      {text}
    </span>
  );
}

/**
 * The one control in this table that is not a read.
 *
 * Native rather than React Aria's `Checkbox` for the same reason the form
 * fields are: a table cell needs a control whose checked state a test can read
 * off the DOM and whose accessible name names the row, not the column.
 */
function RowCheckbox({
  checked,
  indeterminate,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      ref={(node) => {
        if (node) node.indeterminate = Boolean(indeterminate) && !checked;
      }}
      onChange={(event) => onChange(event.target.checked)}
      className="size-4 shrink-0 cursor-pointer rounded accent-brand-solid outline-brand focus-visible:outline-2"
    />
  );
}

function DetailDrawer({
  record,
  actions,
  onClose,
  renderCell,
  columnLabel,
}: {
  record: Record_;
  actions?: ReactNode;
  onClose: () => void;
  renderCell?: (column: string, record: Record_) => ReactNode | undefined;
  columnLabel?: (column: string) => string | undefined;
}) {
  const columns = Object.keys(record);
  return (
    <div
      className="flex w-full flex-col gap-4 rounded-xl border border-secondary bg-secondary p-5"
      aria-label="Record detail"
      role="region"
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-md font-semibold text-primary">Record detail</h2>
        <Button
          size="sm"
          color="tertiary"
          iconLeading={XClose}
          aria-label="Close record detail"
          onClick={onClose}
        />
      </div>
      {/* The drawer is where the whole row is legible, so it is also where an
          action is decided on - the trailing cell is the shortcut, not the
          only way in. */}
      {actions ? <div className="flex flex-col items-start gap-2">{actions}</div> : null}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
        {columns.map((column) => {
          const value = record[column];
          const custom = renderCell?.(column, record);
          return (
            <div key={column} className="flex flex-col gap-1">
              <dt className="text-xs font-medium text-tertiary uppercase">
                {columnLabel?.(column) ?? humanize(column)}
              </dt>
              <dd className="min-w-0">
                {custom !== undefined ? (
                  custom
                ) : isMultiline(value) ? (
                  <pre className="max-h-64 overflow-auto rounded-lg bg-secondary p-3 font-mono text-xs break-words whitespace-pre-wrap text-secondary">
                    {formatFull(column, value)}
                  </pre>
                ) : (
                  <span className="block font-mono text-xs break-all text-secondary">
                    {formatFull(column, value)}
                  </span>
                )}
                {!isEmptyValue(value) ? (
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-brand-secondary underline underline-offset-2"
                    aria-label={`Copy raw ${columnLabel?.(column) ?? humanize(column)}`}
                    onClick={() =>
                      void navigator.clipboard?.writeText(
                        typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value),
                      )
                    }
                  >
                    Copy raw value
                  </button>
                ) : null}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

export function RecordTable({
  records,
  columns: preferred,
  noun,
  nounSingular,
  emptyHint,
  rowActions,
  cellHref,
  renderCell,
  columnLabel,
  selection,
  searchable = true,
}: {
  records: readonly Record_[];
  columns?: readonly string[];
  noun: string;
  /**
   * `noun` in the singular, for the count line. Supplied only where stripping
   * a trailing `s` would be wrong; `1 recommendation runs` shipped because the
   * count line spelled the plural whatever the number was.
   */
  nounSingular?: string;
  emptyHint?: string;
  /**
   * The built-in filter narrows only the rows already on screen. A screen that
   * already searches the whole collection server-side passes `false`, because
   * two search boxes on one page compete: the page-level one asks the server
   * across every record, this one silently filters the current page, and
   * nothing on screen says which of the two an operator just used.
   */
  searchable?: boolean;
  /**
   * A trailing actions cell for one row, repeated in the detail drawer.
   * Omitted entirely when absent, so a read-only table grows no empty column
   * and a viewer is never shown a control that would 403 on click.
   */
  rowActions?: (record: Record_) => ReactNode;
  /** Turn one cell into a link to the page that record belongs to. Returning
   *  nothing leaves the cell as plain text, which is every column by default. */
  cellHref?: (column: string, record: Record_) => string | undefined;
  /**
   * Render one cell - and the same field in the row drawer - as something
   * richer than its text: an app's link-to-the-thing for an id column.
   * Returning `undefined` keeps the default rendering for that cell.
   */
  renderCell?: (column: string, record: Record_) => ReactNode | undefined;
  /** A heading for a column other than its humanised key. */
  columnLabel?: (column: string) => string | undefined;
  /**
   * Multi-select, for the screens whose rows are the subject of one decision
   * taken over many of them at once. Absent by default: a table that offers
   * checkboxes and then nothing to do with them is a control that lies, so the
   * column only exists where a caller holds the selection and acts on it.
   *
   * Selection is held by the caller rather than here because what it means
   * outlives this render - a filter change or a refresh must be able to drop
   * ids the operator can no longer see, and this component has no idea which
   * those are.
   */
  selection?: {
    selected: readonly string[];
    onChange: (next: string[]) => void;
    /** The row's stable id, as the caller's own model names it. */
    idFor: (record: Record_) => string;
    /** Singular noun for the per-row checkbox names, e.g. `garment`. */
    itemNoun?: string;
  };
}) {
  const nounFor = (count: number) =>
    count === 1 ? (nounSingular ?? noun.replace(/(?<![s])s$/, '')) : noun;
  const initialParameters = useMemo(hashParameters, []);
  const [query, setQuery] = useState(() => initialParameters.get('q') ?? '');
  const [sort, setSort] = useState<string | undefined>(
    () => initialParameters.get('sort') ?? undefined,
  );
  const [direction, setDirection] = useState<'asc' | 'desc'>(() =>
    initialParameters.get('direction') === 'desc' ? 'desc' : 'asc',
  );
  const [selected, setSelected] = useState<string | undefined>();
  const deferredQuery = useDeferredValue(query);

  useEffect(() => {
    replaceHashParameters({ q: query || undefined });
  }, [query]);

  useEffect(() => {
    replaceHashParameters({ sort, direction: sort ? direction : undefined });
  }, [sort, direction]);

  const columns = useMemo(
    () => withoutEmptyColumns(orderColumns(records, preferred), records, preferred ?? []),
    [records, preferred],
  );
  const indexed = useMemo(
    () => records.map((record, index) => ({ id: recordIdentity(record, index), record })),
    [records],
  );
  const visible = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    const filtered = needle
      ? indexed.filter((row) => searchText(row.record).includes(needle))
      : [...indexed];
    if (!sort) return filtered;
    return filtered.sort((left, right) => {
      const a = left.record[sort];
      const b = right.record[sort];
      const result =
        typeof a === 'number' && typeof b === 'number'
          ? a - b
          : String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
      return direction === 'asc' ? result : -result;
    });
  }, [indexed, deferredQuery, sort, direction]);

  const selectedRecord = indexed.find((row) => row.id === selected)?.record;

  const chosen = new Set(selection?.selected ?? []);
  const visibleChosenIds = selection
    ? visible.map((row) => selection.idFor(row.record)).filter((id) => chosen.has(id))
    : [];
  const allVisibleChosen = Boolean(
    selection && visible.length > 0 && visibleChosenIds.length === visible.length,
  );
  const itemNoun = selection?.itemNoun ?? nounFor(1);
  const toggleAllVisible = (next: boolean) => {
    if (!selection) return;
    const visibleIds = visible.map((row) => selection.idFor(row.record));
    selection.onChange(
      next
        ? [...selection.selected, ...visibleIds.filter((id) => !chosen.has(id))]
        : selection.selected.filter((id) => !visibleIds.includes(id)),
    );
  };

  if (!records.length) {
    return (
      <Callout tone="neutral" title={`No ${noun} recorded yet`}>
        {emptyHint ?? `Nothing has written to this table. That is not necessarily a problem.`}
      </Callout>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {searchable ? (
            <div className="w-full max-w-xs">
              <Input
                aria-label={`Filter the ${noun} on this page`}
                placeholder={`Filter the ${noun} on this page…`}
                icon={SearchLg}
                value={query}
                onChange={setQuery}
              />
            </div>
          ) : null}
          {searchable || visible.length !== records.length ? (
            <span className="text-sm text-tertiary">
              {visible.length === records.length
                ? `${records.length} ${nounFor(records.length)}`
                : `${visible.length} of ${records.length} ${nounFor(records.length)}`}
            </span>
          ) : null}
        </div>

        <div className="overflow-x-auto rounded-xl border border-secondary bg-primary">
          <table className="w-full min-w-max border-collapse text-left">
            <thead className="bg-secondary">
              <tr>
                {selection ? (
                  <th
                    scope="col"
                    className="w-10 border-b border-secondary px-4 py-2.5 text-xs font-semibold whitespace-nowrap text-tertiary"
                  >
                    <RowCheckbox
                      checked={allVisibleChosen}
                      indeterminate={visibleChosenIds.length > 0}
                      label={`Select every ${noun} on this page`}
                      onChange={toggleAllVisible}
                    />
                  </th>
                ) : null}
                {columns.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-secondary px-4 py-2.5 text-xs font-semibold whitespace-nowrap text-tertiary"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 outline-brand focus-visible:outline-2"
                      aria-label={`Sort by ${columnLabel?.(column) ?? humanize(column)}`}
                      onClick={() => {
                        if (sort === column)
                          setDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                        else {
                          setSort(column);
                          setDirection('asc');
                        }
                      }}
                    >
                      {columnLabel?.(column) ?? humanize(column)}
                      {sort === column ? (direction === 'asc' ? '↑' : '↓') : null}
                    </button>
                  </th>
                ))}
                {rowActions ? (
                  <th
                    scope="col"
                    className="border-b border-secondary px-4 py-2.5 text-xs font-semibold whitespace-nowrap text-tertiary"
                  >
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {visible.flatMap(({ id, record }) => [
                <tr
                  key={id}
                  onClick={() => setSelected(id === selected ? undefined : id)}
                  aria-selected={id === selected}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelected(id === selected ? undefined : id);
                    }
                  }}
                  className={cx(
                    'cursor-pointer border-b border-secondary outline-brand last:border-b-0 hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2',
                    id === selected && 'bg-brand-primary',
                  )}
                >
                  {selection ? (
                    <td
                      className="px-4 py-2.5 align-top"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <RowCheckbox
                        checked={chosen.has(selection.idFor(record))}
                        label={`Select this ${itemNoun}`}
                        onChange={(next) => {
                          const rowId = selection.idFor(record);
                          selection.onChange(
                            next
                              ? [...selection.selected, rowId]
                              : selection.selected.filter((one) => one !== rowId),
                          );
                        }}
                      />
                    </td>
                  ) : null}
                  {columns.map((column) => (
                    <td
                      key={column}
                      className="max-w-xs px-4 py-2.5 align-top text-sm text-secondary"
                    >
                      {renderCell?.(column, record) !== undefined ? (
                        renderCell?.(column, record)
                      ) : cellHref?.(column, record) ? (
                        <a
                          href={cellHref(column, record)}
                          // Inherits the cell's own colour: a linked value is
                          // still the value, so only the dotted underline marks
                          // it as somewhere to go. Brand colour here would make
                          // one field shout over the row it belongs to.
                          className="underline decoration-dotted underline-offset-2"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <CellValue column={column} value={record[column]} />
                        </a>
                      ) : (
                        <CellValue column={column} value={record[column]} />
                      )}
                      {isTimestampKey(column) && typeof record[column] === 'string' ? (
                        <span className="mt-0.5 block text-xs text-quaternary">
                          {formatRelative(record[column] as string)}
                        </span>
                      ) : null}
                    </td>
                  ))}
                  {rowActions ? (
                    // The row itself toggles the drawer, so the actions cell
                    // stops the click rather than opening a drawer behind the
                    // menu the operator just aimed at.
                    <td
                      className="px-4 py-2.5 align-top"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      {rowActions(record)}
                    </td>
                  ) : null}
                </tr>,
                id === selected && selectedRecord ? (
                  <tr key={`${id}-detail`} className="border-b border-secondary last:border-b-0">
                    <td
                      colSpan={columns.length + (rowActions ? 1 : 0) + (selection ? 1 : 0)}
                      className="p-0"
                    >
                      <DetailDrawer
                        record={selectedRecord}
                        {...(renderCell ? { renderCell } : {})}
                        {...(columnLabel ? { columnLabel } : {})}
                        {...(rowActions ? { actions: rowActions(selectedRecord) } : {})}
                        onClose={() => setSelected(undefined)}
                      />
                    </td>
                  </tr>
                ) : null,
              ])}
            </tbody>
          </table>
          {!visible.length ? (
            <p className="px-4 py-6 text-center text-sm text-tertiary">
              No {noun} match “{deferredQuery}”.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
